/* ============================================================
   YUVENLIFE — Cart State Machine  (vanilla, zero-dep)
   ------------------------------------------------------------
   Resilienz-Orchestrierung zwischen UI (cart.js) und API (storefront.js).
   Exponiert window.YuvenCart.

   States (gespiegelt auf <html data-cart-status>):
     idle · adding · syncing · error · degraded · redirecting

   Garantien:
   · Der/die User landet NIE auf einem stillen kaputten Zustand —
     jeder terminale Fehler setzt error/degraded + sichtbarer Grund.
   · Abgelaufener/ungültiger Cart wird transparent neu erstellt.
   · Degraded-Mode (Storefront unerreichbar): lokaler Cart bleibt nutzbar,
     Checkout deaktiviert, Retry-Timer flusht bei Erreichbarkeit.
   · Mutationen sind serialisiert (Queue) → keine Races, Double-Submit-sicher.
   · Totals/Lines kommen IMMER aus dem confirmed Shopify-Cart → können nie
     vom Checkout abweichen. Nur im Degraded-Fallback lokal geschätzt.
   ============================================================ */
(function () {
  "use strict";

  var CFG = window.YUVENLIFE_SHOP;
  var SF  = window.YuvenStorefront;
  if (!CFG || !SF) { console.error("[yuven-cart] config/storefront fehlt — Cart inaktiv."); return; }

  var htmlEl   = document.documentElement;
  /* v2 trennt alte Warenkörbe mit den früheren Sammelprodukt-Varianten ab.
     Dadurch werden verwaiste 0-Euro-Zeilen nicht in die neue Struktur übernommen. */
  var LS_KEY   = "yl-cart-id-v3";
  var DEGRADE_RETRY = 15000; // ms

  /* ── interner State ──────────────────────────────────────── */
  var state = {
    status: "idle",         // idle|adding|syncing|error|degraded|redirecting
    cartId: null,
    checkoutUrl: null,
    lines: [],              // normalisiert: {plan, cartLineId, qty, title, meta, priceEach, lineTotal, image, isSub, available}
    total: 0,               // confirmed (oder lokal geschätzt im degraded)
    tax: 0,                 // enthaltene MwSt (DE tax-inclusive) aus Shopify
    currency: CFG.currencyCode,
    discountCodes: [],
    message: "",            // user-sichtbarer Grund bei error/degraded
    localMode: false        // true = Lines sind lokaler Spiegel (Storefront down)
  };
  var localCart = [];       // [{plan, qty}] — nur im degraded/local-Fallback genutzt
  var priceMap  = {};       // variantGid → {amount:Number, available:Bool}
  var listeners = [];
  var degradeTimer = null;

  // Cart/Checkout-Bilder = unsere Storefront-Keyvisuals (Plan-genau; Shopify-Produktbilder uneindeutig,
  // da testen+starter3 am selben Produkt hängen). /bilder-Pfade werden im Shopify-Theme auf CDN gemappt.
  var PLAN_IMG = {
    testen:    ((window.YL_ASSET_BASE||"")+"card-einzel.png"),
    routine30: ((window.YL_ASSET_BASE||"")+"card-abo.png"),
    starter3:  ((window.YL_ASSET_BASE||"")+"card-start.png"),
    paket6:    "https://cdn.shopify.com/s/files/1/0885/9186/0041/files/Flaschen_5_1.png?v=1784277495",
    travel:    "https://cdn.shopify.com/s/files/1/0885/9186/0041/files/yuvenlife-100ml-travel-size_10fe3a8e-b1bc-46f5-b1bd-d0469024e9eb.png?v=1784280479"
  };

  // Plan-Auflösung: Variant + SellingPlan. testen/routine30 teilen sich die 500ml-Variante
  // und unterscheiden sich nur über den SellingPlan → exakt matchen, sonst Variant-Fallback.
  function resolvePlan(variantGid, planGid) {
    var keys = Object.keys(CFG.map), i, m;
    for (i = 0; i < keys.length; i++) { m = CFG.map[keys[i]]; if (m.variantGid === variantGid && (m.sellingPlanGid || null) === (planGid || null)) return keys[i]; }
    for (i = 0; i < keys.length; i++) { if (CFG.map[keys[i]].variantGid === variantGid) return keys[i]; }
    return null;
  }

  /* ── Helpers ─────────────────────────────────────────────── */
  function log() { if (CFG.flags && CFG.flags.debug) console.log.apply(console, ["[yuven-cart]"].concat([].slice.call(arguments))); }
  function lsGet() { try { return localStorage.getItem(LS_KEY); } catch (e) { return null; } }
  function lsSet(v) { try { v ? localStorage.setItem(LS_KEY, v) : localStorage.removeItem(LS_KEY); } catch (e) {} }
  function num(x) { var n = parseFloat(x); return isNaN(n) ? 0 : n; }
  function planMeta(plan) { return (CFG.fallback && CFG.fallback[plan]) || { title: plan, meta: "", price: 0, ship: 0 }; }

  function analyticsAllowed() {
    try {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      return !!(privacy && privacy.analyticsProcessingAllowed && privacy.analyticsProcessingAllowed() === true);
    } catch (error) { return false; }
  }

  function analyticsStatus(status) {
    if (window.Shopify && window.Shopify.theme && window.Shopify.theme.role !== "main") {
      htmlEl.setAttribute("data-yl-cart-analytics", status);
    }
  }

  function trackConfirmedAddition(cart, plan, requested, beforeQuantity, previousCartId, allowedAtStart) {
    if (cart && cart.native) { analyticsStatus("native-shopify-cart"); return; }
    try {
      if (!allowedAtStart || !analyticsAllowed()) { analyticsStatus("consent-not-granted"); return; }
      var mapping = CFG.map[plan];
      var entry = ((cart.lines && cart.lines.nodes) || []).filter(function (row) {
        var sellingPlan = row.sellingPlanAllocation && row.sellingPlanAllocation.sellingPlan;
        return row.merchandise && row.merchandise.id === mapping.variantGid &&
          ((sellingPlan && sellingPlan.id) || null) === (mapping.sellingPlanGid || null);
      })[0];
      var normalized = state.lines.filter(function (row) { return row.plan === plan; })[0];
      if (!entry || !normalized) { analyticsStatus("line-not-found"); return; }
      var prior = previousCartId === cart.id ? beforeQuantity : 0;
      var quantity = Math.min(requested, normalized.qty - prior);
      if (quantity <= 0) { analyticsStatus("no-quantity-increase"); return; }
      var variant = entry.merchandise;
      var product = variant.product || {};
      var productMatch = /^gid:\/\/shopify\/Product\/(\d+)$/.exec(product.id || "");
      var variantMatch = /^gid:\/\/shopify\/ProductVariant\/(\d+)$/.exec(variant.id || "");
      if (!productMatch || !variantMatch) { analyticsStatus("missing-product-identifiers"); return; }
      var library = window.ShopifyAnalytics && window.ShopifyAnalytics.lib;
      if (!library || typeof library.track !== "function") { analyticsStatus("tracker-unavailable"); return; }
      var properties = {
        productId: productMatch[1], variantId: variantMatch[1],
        quantity: String(quantity), price: normalized.priceEach,
        currency: state.currency, name: product.title || normalized.title,
        variant: variant.title || "", sku: variant.sku || "",
        brand: product.vendor || "", category: product.productType || ""
      };
      var page = window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.page;
      if (page) {
        properties.pageType = page.pageType;
        properties.resourceType = page.resourceType;
        properties.resourceId = page.resourceId;
      }
      library.track("Added Product", properties);
      analyticsStatus("submitted-to-shopify");
    } catch (error) { analyticsStatus("tracker-error"); }
  }

  function setStatus(s, message) {
    state.status = s;
    state.message = message || "";
    htmlEl.setAttribute("data-cart-status", s);
    emit();
  }
  function emit() { var snap = getState(); listeners.forEach(function (fn) { try { fn(snap); } catch (e) {} }); }

  function getState() {
    return {
      status: state.status,
      message: state.message,
      localMode: state.localMode,
      lines: state.lines.slice(),
      count: state.lines.reduce(function (a, l) { return a + l.qty; }, 0),
      total: state.total,
      tax: state.tax,
      currency: state.currency,
      checkoutUrl: state.checkoutUrl,
      discountCodes: state.discountCodes.slice(),
      canCheckout: !state.localMode && !!state.checkoutUrl && state.lines.length > 0 &&
                   state.status !== "redirecting" && state.status !== "error"
    };
  }

  /* ── Confirmed Shopify-Cart → normalisierte Lines übernehmen ── */
  function adoptCart(cart) {
    state.localMode = false;
    localCart = [];
    state.cartId = cart.id;
    state.checkoutUrl = cart.checkoutUrl || null;
    state.total = cart.cost && cart.cost.totalAmount ? num(cart.cost.totalAmount.amount) : 0;
    state.tax = cart.cost && cart.cost.totalTaxAmount ? num(cart.cost.totalTaxAmount.amount) : 0;
    state.currency = (cart.cost && cart.cost.totalAmount && cart.cost.totalAmount.currencyCode) || CFG.currencyCode;
    state.discountCodes = (cart.discountCodes || []).map(function (d) { return { code: d.code, applicable: d.applicable !== false }; });
    var nodes = (cart.lines && cart.lines.nodes) || [];
    state.lines = nodes.map(function (n) {
      var m = n.merchandise || {};
      var vid = m.id;
      var planGid = (n.sellingPlanAllocation && n.sellingPlanAllocation.sellingPlan && n.sellingPlanAllocation.sellingPlan.id) || null;
      var plan = resolvePlan(vid, planGid);
      var fb = plan ? planMeta(plan) : { title: (m.product && m.product.title) || "", meta: "" };
      var alloc = n.sellingPlanAllocation;
      var isSub = !!(alloc && alloc.sellingPlan);
      /* Abo-Zeilen: Appstle liefert quantity/cost=0 am Cart-Node, der reale
         wiederkehrende Preis steht in sellingPlanAllocation.priceAdjustments.
         → Preis + Menge daraus ableiten, sonst zeigt der Drawer 0,00 €. */
      var subPrice = (isSub && alloc.priceAdjustments && alloc.priceAdjustments[0] && alloc.priceAdjustments[0].price)
        ? num(alloc.priceAdjustments[0].price.amount) : null;
      var qty = n.quantity || (isSub ? 1 : 0);
      var each = subPrice != null ? subPrice : (m.price ? num(m.price.amount) : 0);
      var hasDiscount = !!(n.discountAllocations && n.discountAllocations.length);
      var lineCost = n.cost && n.cost.totalAmount ? num(n.cost.totalAmount.amount) : 0;
      var lineTotal = isSub ? each * qty : ((hasDiscount || lineCost) ? lineCost : each * qty);
      return {
        plan: plan,
        cartLineId: n.id,
        qty: qty,
        title: fb.title,
        meta: fb.meta,
        priceEach: each,
        lineTotal: lineTotal,
        image: PLAN_IMG[plan] || (m.product && m.product.featuredImage && m.product.featuredImage.url) || ((window.YL_ASSET_BASE||"")+"card-einzel.png"),
        isSub: isSub,
        available: m.availableForSale !== false
      };
    });
    /* Cart-Gesamtsumme: cart.cost.totalAmount ist bei reinen Abo-Carts 0 (s.o.).
       → aus den normalisierten Line-Totals summieren, damit der Drawer stimmt. */
    var lineSum = state.lines.reduce(function (s, l) { return s + (l.lineTotal || 0); }, 0);
    if (!state.total || lineSum > state.total) state.total = lineSum;
    lsSet(state.cartId);
  }

  /* ── Lokaler Fallback-Cart (degraded) → Lines schätzen ────── */
  function renderLocal() {
    state.localMode = true;
    state.checkoutUrl = null;
    var total = 0;
    state.lines = localCart.map(function (l) {
      var fb = planMeta(l.plan);
      var pm = priceMap[CFG.map[l.plan] && CFG.map[l.plan].variantGid];
      var each = pm ? pm.amount : fb.price;
      var lineTotal = (each + (fb.ship || 0)) * l.qty;
      total += lineTotal;
      return {
        plan: l.plan, cartLineId: null, qty: l.qty, title: fb.title, meta: fb.meta,
        priceEach: each, lineTotal: lineTotal,
        image: PLAN_IMG[l.plan] || ((window.YL_ASSET_BASE||"")+"card-einzel.png"), isSub: CFG.map[l.plan] && CFG.map[l.plan].type === "subscription",
        available: true
      };
    });
    state.total = total;
    state.tax = 0;
  }

  function looksLikeMissingCart(res) { return !res.networkError && !res.cart && res.ok; }

  /* ── Degraded eintreten + Retry planen ─────────────────────── */
  function enterDegraded(msg) {
    renderLocal();
    setStatus("degraded", msg || "verbindung zum shop gerade nicht möglich — dein warenkorb bleibt erhalten.");
    if (!degradeTimer) {
      degradeTimer = setInterval(tryRecover, DEGRADE_RETRY);
    }
  }
  function clearDegrade() { if (degradeTimer) { clearInterval(degradeTimer); degradeTimer = null; } }

  function tryRecover() {
    if (!state.localMode) { clearDegrade(); return; }
    if (!localCart.length) { clearDegrade(); state.lines = []; state.total = 0; setStatus("idle"); return; }
    log("degraded recover: flush local cart");
    var lines = localCart.map(function (l) { return SF.buildLine(l.plan, l.qty); }).filter(Boolean);
    SF.cartCreate(lines).then(function (res) {
      if (res.ok && res.cart) { clearDegrade(); adoptCart(res.cart); setStatus("idle"); }
      // sonst: bleibt degraded, nächster Tick versucht erneut
    });
  }

  /* ── serialisierte Mutation-Queue (verhindert Races/Double-Submit) ── */
  var queue = Promise.resolve();
  function enqueue(task) { var p = queue.then(task, task); queue = p.catch(function () {}); return p; }

  /* ── Mutation: hinzufügen / Cart anlegen mit Recovery ──────── */
  function mutateAddOrCreate(line) {
    if (!state.cartId) {
      return SF.cartCreate([line]).then(function (res) {
        if (looksLikeMissingCart(res)) { return SF.cartCreate([line]); }
        return res;
      });
    }
    return SF.cartLinesAdd(state.cartId, [line]).then(function (res) {
      if (res.ok && res.cart) return res;
      if (looksLikeMissingCart(res) || (!res.networkError && !res.cart)) {
        log("cart missing on add → recreate");
        lsSet(null); state.cartId = null;
        return SF.cartCreate([line]);
      }
      return res;
    });
  }

  /* ── Public: add ───────────────────────────────────────────── */
  function add(plan) {
    var allowedAtStart = analyticsAllowed();
    if (!CFG.map[plan]) { log("unknown plan", plan); return Promise.resolve(); }
    return enqueue(function () {
      // im Degraded: lokal weiterführen, nicht blocken
      if (state.localMode) {
        bumpLocal(plan, 1); renderLocal(); setStatus("degraded", state.message); return;
      }
      var line = SF.buildLine(plan, 1);
      if (!line) { setStatus("error", "produkt nicht verfügbar."); return; }
      var previousCartId = state.cartId;
      var existing = state.lines.filter(function (row) { return row.plan === plan; })[0];
      var beforeQuantity = existing ? existing.qty : 0;
      setStatus("adding");
      return mutateAddOrCreate(line).then(function (res) {
        if (res.ok && res.cart) {
          adoptCart(res.cart);
          trackConfirmedAddition(res.cart, plan, 1, beforeQuantity, previousCartId, allowedAtStart);
          setStatus("idle"); return;
        }
        if (res.networkError) { bumpLocal(plan, 1); enterDegraded(); return; }
        if (res.userErrors && res.userErrors.length) { setStatus("error", friendly(res.userErrors)); return; }
        setStatus("error", "konnte nicht hinzugefügt werden. bitte erneut versuchen.");
      });
    });
  }

  function bumpLocal(plan, d) {
    var l = localCart.filter(function (x) { return x.plan === plan; })[0];
    if (l) l.qty = Math.max(0, l.qty + d); else if (d > 0) localCart.push({ plan: plan, qty: d });
    localCart = localCart.filter(function (x) { return x.qty > 0; });
  }

  /* ── Public: removeByPlan ──────────────────────────────────── */
  function removeByPlan(plan) {
    return enqueue(function () {
      if (state.localMode) { bumpLocal(plan, -9999); renderLocal(); setStatus("degraded", state.message); return; }
      var line = state.lines.filter(function (l) { return l.plan === plan; })[0];
      if (!line || !state.cartId || !line.cartLineId) { return; }
      setStatus("adding");
      return SF.cartLinesRemove(state.cartId, [line.cartLineId]).then(function (res) {
        if (res.ok && res.cart) { adoptCart(res.cart); setStatus("idle"); return; }
        if (res.networkError) { enterDegraded(); return; }
        setStatus("error", friendly(res.userErrors) || "konnte nicht entfernt werden.");
      });
    });
  }

  /* ── Public: setQty (für spätere Stepper) ──────────────────── */
  function setQty(plan, qty) {
    var allowedAtStart = analyticsAllowed();
    return enqueue(function () {
      var line = state.lines.filter(function (l) { return l.plan === plan; })[0];
      if (state.localMode || !line || !state.cartId || !line.cartLineId) return;
      if (qty <= 0) return removeByPlan(plan);
      setStatus("adding");
      var upd = { id: line.cartLineId, quantity: qty };
      var m = CFG.map[plan];
      if (CFG.flags.subscriptionsEnabled && m && m.type === "subscription" && m.sellingPlanGid) upd.sellingPlanId = m.sellingPlanGid;
      var previousCartId = state.cartId;
      var beforeQuantity = line.qty;
      return SF.cartLinesUpdate(state.cartId, [upd]).then(function (res) {
        if (res.ok && res.cart) {
          adoptCart(res.cart);
          trackConfirmedAddition(res.cart, plan, Math.max(0, qty - beforeQuantity), beforeQuantity, previousCartId, allowedAtStart);
          setStatus("idle"); return;
        }
        if (res.networkError) { enterDegraded(); return; }
        setStatus("error", friendly(res.userErrors) || "menge konnte nicht geändert werden.");
      });
    });
  }

  /* ── Public: checkout ──────────────────────────────────────── */
  function checkout() {
    if (state.localMode) { setStatus("degraded", "checkout gerade nicht möglich — bitte in einem moment erneut versuchen."); return; }
    if (!state.checkoutUrl || !state.lines.length) { setStatus("error", "warenkorb leer oder checkout nicht bereit."); return; }
    if (!safeCheckoutUrl(state.checkoutUrl)) { setStatus("error", "checkout-adresse ungültig."); return; }
    setStatus("redirecting");
    window.location.assign(state.checkoutUrl);
  }

  /* ── Rabattcode-Brücke: YL_100_TSG ergänzt erst nach Codeeingabe
     die echte 100-ml-Variante und übergibt den Code an Shopify. ── */
  function rejectDiscount(code, message) {
    if (code !== "PIONIER10") {
      setStatus("error", message);
      return Promise.resolve(false);
    }
    return SF.cartDiscountCodesUpdate(state.cartId, []).then(function (cleared) {
      if (cleared && cleared.ok && cleared.cart) adoptCart(cleared.cart);
      setStatus("pioneer_closed", "PIONEER10_CLOSED");
      return false;
    }, function () {
      setStatus("pioneer_closed", "PIONEER10_CLOSED");
      return false;
    });
  }

  function applyDiscount(code) {
    code = String(code || "").trim().toUpperCase();
    if (!code) { setStatus("error", "bitte rabattcode eingeben."); return Promise.resolve(false); }
    if (!state.cartId || !state.lines.length) { setStatus("error", "bitte zuerst ein produkt in den warenkorb legen."); return Promise.resolve(false); }
    return enqueue(function () {
      setStatus("syncing");
      var giftAdded = false;
      var hasTravel = state.lines.some(function (l) { return l.plan === "travel"; });
      var prep = Promise.resolve({ ok: true, cart: null });
      if (code === "YL_100_TSG" && !hasTravel) {
        giftAdded = true;
        prep = SF.cartLinesAdd(state.cartId, [SF.buildLine("travel", 1)]);
      }
      return prep.then(function (res) {
        if (res && res.cart) adoptCart(res.cart);
        return SF.cartDiscountCodesUpdate(state.cartId, [code]);
      }).then(function (res) {
        if (!(res.ok && res.cart)) return rejectDiscount(code, friendly(res.userErrors) || "rabattcode konnte nicht angewendet werden.");
        adoptCart(res.cart);
        var applied = state.discountCodes.some(function (d) { return d.code.toUpperCase() === code && d.applicable; });
        if (applied) { setStatus("idle", "rabattcode angewendet."); return true; }
        if (giftAdded) {
          var gift = state.lines.filter(function (l) { return l.plan === "travel"; })[0];
          if (gift && gift.cartLineId) return SF.cartLinesRemove(state.cartId, [gift.cartLineId]).then(function (rr) {
            if (rr.ok && rr.cart) adoptCart(rr.cart);
            return rejectDiscount(code, "rabattcode ist für diesen warenkorb nicht gültig.");
          });
        }
        return rejectDiscount(code, "rabattcode ist für diesen warenkorb nicht gültig.");
      });
    });
  }

  function safeCheckoutUrl(url) {
    try {
      var u = new URL(url);
      if (u.protocol !== "https:") return false;
      var host = u.hostname.toLowerCase();
      return host === CFG.shopDomain.toLowerCase() ||
             host.indexOf(".myshopify.com") !== -1 ||
             host.indexOf("yuvenlife.com") !== -1 ||
             host.indexOf("shopify.com") !== -1; // shopify checkout-subdomains
    } catch (e) { return false; }
  }

  /* ── userError-Codes → freundliche DE-Meldung ──────────────── */
  function friendly(ues) {
    if (!ues || !ues.length) return "";
    var code = (ues[0].code || "").toUpperCase();
    var map = {
      MERCHANDISE_OUT_OF_STOCK: "artikel aktuell nicht verfügbar.",
      MERCHANDISE_NOT_ENOUGH_STOCK: "nicht genügend bestand.",
      INVALID: "eingabe ungültig — warenkorb wird zurückgesetzt.",
      LOW_QUANTITY: "mindestmenge nicht erreicht."
    };
    return map[code] || (ues[0].message || "").toLowerCase() || "etwas ist schiefgelaufen.";
  }

  /* ── Live-Preise laden (best effort) ───────────────────────── */
  function loadPrices() {
    if (!(CFG.flags && CFG.flags.livePrices)) return Promise.resolve();
    var ids = Object.keys(CFG.map).map(function (p) { return CFG.map[p].variantGid; });
    return SF.fetchPrices(ids).then(function (r) {
      if (r.ok) {
        r.nodes.forEach(function (n) {
          if (n && n.id && n.price) priceMap[n.id] = { amount: num(n.price.amount), available: n.availableForSale !== false };
        });
        emit(); // UI kann Preise auffrischen
      }
    });
  }
  function priceForPlan(plan) {
    var m = CFG.map[plan]; if (!m) return null;
    var pm = priceMap[m.variantGid];
    return pm ? pm.amount : null;
  }

  /* ── Init: Cart aus localStorage hydrieren + Preise ────────── */
  function init() {
    try {
      if (window.Shopify && !window.Shopify.customerPrivacy && typeof window.Shopify.loadFeatures === "function") {
        window.Shopify.loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function () {});
      }
    } catch (error) {}
    var saved = lsGet();
    loadPrices();
    if (!saved && !SF.nativeCart) { setStatus("idle"); return; }
    setStatus("syncing");
    SF.getCart(saved).then(function (r) {
      if (r.ok && r.cart) { adoptCart(r.cart); setStatus("idle"); return; }
      if (r.ok && !r.cart) { lsSet(null); state.cartId = null; state.lines = []; state.total = 0; setStatus("idle"); return; } // abgelaufen
      // network: leeren, aber NICHT als Fehler dem User zeigen (Cart war eh leer-startbar)
      lsSet(null); state.cartId = null; setStatus("idle");
    });
  }

  window.YuvenCart = {
    init: init,
    onChange: function (fn) { listeners.push(fn); return function () { listeners = listeners.filter(function (f) { return f !== fn; }); }; },
    getState: getState,
    add: add,
    removeByPlan: removeByPlan,
    setQty: setQty,
    checkout: checkout,
    applyDiscount: applyDiscount,
    priceForPlan: priceForPlan,
    retry: tryRecover
  };
})();
